import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Space } from 'antd';
import { capitalize } from 'lodash';
import { AxiosError } from 'axios';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { DeclarationCountry, declarationCountryLabels } from 'store/declarations/enums/common/declaration-country';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import Button from 'components/new-ui/base/button/Button';
import { H2 } from 'components/new-ui/base/typography/styles';
import useTeams from 'hooks/useTeams';

type Props = {
    teamId: string;
    initialValues: FormValues;
    teamManagerOptions: SelectOption[];
    onSuccess?: () => void;
    onCancel?: () => void;
};

const validationSchema = Yup.object({
    name: Yup.string().trim().nullable().required(getRequiredMessage('team name')),
    managerId: Yup.string().trim().nullable().required(getRequiredMessage('team manager')),
    market: Yup.string().trim().nullable().required(getRequiredMessage('market')),
}).required();

type FormValues = Yup.InferType<typeof validationSchema>;

const UpdateTeamForm = ({ teamId, initialValues, teamManagerOptions, onSuccess, onCancel }: Props) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { updateTeam } = useTeams();

    const {
        dirty,
        isSubmitting,
        isValid,
        handleSubmit,
        getFieldHelpers,
        getFieldProps,
        getFieldMeta,
        submitForm,
        setFieldValue,
        resetForm,
    } = useFormik<FormValues>({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });
                await updateTeam(teamId, {
                    market: values.market as DeclarationCountry,
                    name: values.name,
                    teamManager: values.managerId,
                });
                Notification({
                    placement: 'top',
                    type: 'success',
                    messageTitle: 'Editing Success',
                    description: 'Successfully updated team information',
                });
                onSuccess?.();
            } catch (error) {
                const errorInfo = error instanceof AxiosError && error?.response?.data?.message;
                Notification({
                    placement: 'top',
                    type: 'error',
                    messageTitle: 'Editing Error',
                    description: errorInfo ?? 'Could not edit team. Please try again',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const handleSubmitForm = async () => {
        await submitForm();
    };

    return (
        <div
            style={{
                display: 'flex',
                width: '100%',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
            }}
        >
            <Space direction="vertical" size={16}>
                <H2>Team Details</H2>
                <form onSubmit={handleSubmit}>
                    <Space
                        direction="vertical"
                        style={{
                            minWidth: 420,
                        }}
                        size={16}
                    >
                        <div>
                            <label style={{ marginBottom: 8 }} htmlFor="name">
                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                Team Name
                            </label>
                            <FormInput
                                required
                                placeholder="Team Name"
                                fieldProps={getFieldProps('name')}
                                fieldHelper={getFieldHelpers('name')}
                                fieldMeta={getFieldMeta('name')}
                            />
                        </div>

                        <div>
                            <label style={{ marginBottom: 8 }} htmlFor="managerId">
                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                Team Manager
                            </label>
                            <FormSelect
                                hideKeys
                                placeholder="Team Manager"
                                fieldHelper={getFieldHelpers('managerId')}
                                fieldMeta={getFieldMeta('managerId')}
                                fieldProps={getFieldProps('managerId')}
                                selectOptions={teamManagerOptions}
                                onClear={() => {
                                    setFieldValue('managerId', undefined);
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ marginBottom: 8 }} htmlFor="market">
                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                Market
                            </label>
                            <FormSelect
                                hideKeys
                                placeholder="Market"
                                fieldProps={getFieldProps('market')}
                                fieldHelper={getFieldHelpers('market')}
                                fieldMeta={getFieldMeta('market')}
                                selectOptions={declarationCountryLabels.map((el) => ({
                                    id: el.id,
                                    value: capitalize(el.value),
                                }))}
                            />
                        </div>
                    </Space>
                </form>
            </Space>
            <Space size={8}>
                <Button
                    onClick={() => {
                        resetForm();
                        onCancel?.();
                    }}
                >
                    Cancel
                </Button>
                <Button type="primary" loading={isSubmitting} disabled={!isValid && dirty} onClick={handleSubmitForm}>
                    Save
                </Button>
            </Space>
        </div>
    );
};

export default UpdateTeamForm;
